// @ts-nocheck
import {
  effectiveLppWithdrawalAge,
  lppCapitalForWithdrawalAge,
  resolveThirdPillarCapital,
  yearOfIso,
} from "./planificationSaisie";
import { lppWithdrawalDate } from "./avsAge";
import type {
  AnalyseInput,
  CapitalKind,
  PersonComputed,
  PersonInput,
  PersonKey,
  WithdrawalPlanItem,
  WithdrawalScenario,
} from "./types";
import { RETIREMENT_AGES } from "./types";

export function lppPlanYear(
  input: AnalyseInput,
  item: { kind: string; titulaire?: string; sourceId?: string },
): number | null {
  if (item.kind !== "lpp") return null;
  const person = item.titulaire === "conjoint" ? input.conjoint : input.client1;
  if (!person) return null;
  let age = effectiveLppWithdrawalAge(person, dossierLppAge(input));
  if (item.sourceId) {
    const avoir = (person.lppAvoirs || []).find((row) => row.id === item.sourceId);
    if (avoir?.ageRetrait != null && Number.isFinite(Number(avoir.ageRetrait))) {
      age = Number(avoir.ageRetrait);
    }
  }
  return lppWithdrawalDate(person.dateNaissance, age)?.getFullYear() ?? null;
}

function finiteYear(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Number(value);
}

/** Année affichée et enregistrée : capital du tableau à « 1re possible », sinon onglet LPP. */
export function lppScenarioYear(
  input: AnalyseInput,
  item: WithdrawalPlanItem,
): number | null {
  if (item.kind !== "lpp") return selectedWithdrawalYear(item);
  const requested = finiteYear(item.anneePremierePossible);
  if (requested != null) {
    const person = item.titulaire === "conjoint" ? input.conjoint : input.client1;
    if (lppTableCapitalForYear(person, item.sourceId, requested).found) return requested;
  }
  return lppPlanYear(input, item) ?? selectedWithdrawalYear(item);
}

export function lppAgeForCalendarYear(
  birth: string | null | undefined,
  year: number | null | undefined,
  ages?: number[],
): number | null {
  if (year == null || !Number.isFinite(Number(year))) return null;
  const target = Number(year);
  const list = (ages && ages.length ? ages : [...RETIREMENT_AGES])
    .map((age) => Number(age))
    .filter((age) => Number.isFinite(age));
  const unique = [...new Set(list)].sort((a, b) => b - a);
  for (const age of unique) {
    const start = lppWithdrawalDate(birth, age);
    if (start && start.getFullYear() === target) return age;
  }
  return null;
}

/** Capital du tableau LPP pour l'année demandée. Vide ou hors tableau : pas de montant inventé. */
export function lppTableCapitalForYear(
  person: PersonInput | null | undefined,
  sourceId: string | null | undefined,
  year: number,
): { age: number | null; capital: number; found: boolean } {
  if (!person) return { age: null, capital: 0, found: false };
  const rowsForAges =
    sourceId && sourceId !== "primary"
      ? (person.lppAvoirs || []).find((row) => row.id === sourceId)?.rows
      : person.lpp;
  const ages = (rowsForAges || [])
    .map((row) => Number(row.age))
    .filter((age) => Number.isFinite(age));
  const age = lppAgeForCalendarYear(
    person.dateNaissance,
    year,
    ages.length ? ages : undefined,
  );
  if (age == null) return { age: null, capital: 0, found: false };
  const rows =
    sourceId && sourceId !== "primary"
      ? (person.lppAvoirs || []).find((row) => row.id === sourceId)?.rows
      : person.lpp;
  const row = lppCapitalForWithdrawalAge(rows, age);
  if (!row.found || !(row.capital > 0)) return { age, capital: 0, found: false };
  return { age, capital: row.capital, found: true };
}

/**
 * Nouveau disponible = capital du tableau à l'âge de l'année.
 * Un montant retiré déjà inférieur au disponible est conservé s'il ne dépasse pas le nouveau disponible.
 */
export function lppAmountsForScenarioYear(
  item: { montantDisponible?: number | null; pctCapital?: number | null },
  newDispo: number,
): { montantDisponible: number; pctCapital: number } {
  const oldDispo = Number(item.montantDisponible) || 0;
  const pct = Math.min(100, Math.max(0, Number(item.pctCapital) || 0));
  const oldRetire = Math.round(oldDispo * (pct / 100) * 100) / 100;
  const partial = oldDispo > 0.009 && oldRetire < oldDispo - 0.009;
  if (!partial) return { montantDisponible: newDispo, pctCapital: 100 };
  const kept = Math.min(oldRetire, newDispo);
  return {
    montantDisponible: newDispo,
    pctCapital: newDispo > 0 ? (kept / newDispo) * 100 : 0,
  };
}

function dossierLppAge(input: AnalyseInput): number | null {
  const age = input.ageRetraiteSouhaite ?? input.client1?.ageRetraiteSouhaite ?? null;
  return age != null && Number.isFinite(Number(age)) ? Number(age) : null;
}

function lppItem(
  id: string,
  titulaire: PersonKey,
  input: AnalyseInput,
  person: PersonInput,
  computed: PersonComputed,
): WithdrawalPlanItem {
  const age = effectiveLppWithdrawalAge(person, dossierLppAge(input));
  const row = lppCapitalForWithdrawalAge(person.lpp, age);
  const year =
    lppWithdrawalDate(person.dateNaissance, age)?.getFullYear() ??
    yearOfIso(computed.dateFinActivite || computed.dateDepart);
  const custom =
    person.ageRetraitLppSouhaite != null &&
    Number.isFinite(Number(person.ageRetraitLppSouhaite));
  return {
    id,
    kind: "lpp",
    titulaire,
    label: `LPP ${person.prenom || (titulaire === "client1" ? "assuré 1" : "Assuré 2")}`,
    institution: "LPP",
    montantDisponible: row.found ? row.capital : custom ? 0 : computed.capitalLpp65,
    pctCapital: computed.lppPctDeblocable,
    anneeRetraitPrevue: year,
    anneePremierePossible: year,
    anneeDernierePossible: null,
  };
}

function extraLppItems(
  titulaire: PersonKey,
  person: PersonInput,
  dossierAge: number | null,
): WithdrawalPlanItem[] {
  const personAge = effectiveLppWithdrawalAge(person, dossierAge);
  return (person.lppAvoirs || []).map((avoir) => {
    const age =
      avoir.ageRetrait != null && Number.isFinite(Number(avoir.ageRetrait))
        ? Number(avoir.ageRetrait)
        : personAge;
    const row = lppCapitalForWithdrawalAge(avoir.rows, age);
    const etatique = avoir.type === "etatique";
    const year = lppWithdrawalDate(person.dateNaissance, age)?.getFullYear() ?? null;
    return {
      id: `lpp-${avoir.id}`,
      kind: "lpp" as const,
      titulaire,
      label: `LPP ${avoir.institution || person.prenom || ""}`.trim(),
      institution: avoir.institution || "LPP",
      montantDisponible: row.found ? row.capital : 0,
      pctCapital: etatique ? 100 : avoir.pctDeblocable ?? 100,
      anneeRetraitPrevue: year,
      anneePremierePossible: year,
      anneeDernierePossible: null,
      sourceId: avoir.id,
    };
  });
}

/**
 * Construit des items de planification par défaut à partir du dossier
 * (LPP, LP, 3P) — sans inventer de règles légales de dates.
 * Fichier sans dépendance ESTV (utilisable côté client).
 */
export function buildDefaultWithdrawalItems(
  input: AnalyseInput,
  client1: PersonComputed,
  conjoint: PersonComputed | null,
): WithdrawalPlanItem[] {
  const items: WithdrawalPlanItem[] = [];
  const yearOf = (iso: string | null | undefined) =>
    iso && /^\d{4}/.test(iso) ? Number(iso.slice(0, 4)) : null;

  items.push(lppItem("lpp-client1", "client1", input, input.client1, client1));
  items.push(...extraLppItems("client1", input.client1, dossierLppAge(input)));

  if (conjoint && input.conjoint) {
    items.push(lppItem("lpp-conjoint", "conjoint", input, input.conjoint, conjoint));
    items.push(...extraLppItems("conjoint", input.conjoint, dossierLppAge(input)));
  }

  for (const lp of input.libresPassages || []) {
    items.push({
      id: `lp-${lp.id}`,
      kind: "libre_passage",
      titulaire: lp.titulaire,
      label: `Libre passage ${lp.institution || ""}`.trim(),
      institution: lp.institution || "Libre passage",
      montantDisponible: lp.montant,
      pctCapital: 100,
      anneeRetraitPrevue: (() => {
        const fromDate = yearOf(lp.dateRetraitPossible);
        if (fromDate) return fromDate;
        if (lp.ageDeblocage == null) return null;
        const birth = yearOf(
          (lp.titulaire === "conjoint" ? input.conjoint : input.client1)
            ?.dateNaissance,
        );
        return birth == null ? null : birth + Number(lp.ageDeblocage);
      })(),
      anneePremierePossible: (() => {
        const fromDate = yearOf(lp.dateRetraitPossible);
        if (fromDate) return fromDate;
        if (lp.ageDeblocage == null) return null;
        const birth = yearOf(
          (lp.titulaire === "conjoint" ? input.conjoint : input.client1)
            ?.dateNaissance,
        );
        return birth == null ? null : birth + Number(lp.ageDeblocage);
      })(),
      anneeDernierePossible: null,
      sourceId: lp.id,
    });
  }

  const push3p = (
    titulaire: PersonKey,
    prenom: string,
    contracts: AnalyseInput["client1"]["troisiemePilier"],
  ) => {
    for (const c of contracts || []) {
      const year = yearOf(c.echeance);
      const resolved = resolveThirdPillarCapital(c, year);
      if (!c.montant && !(c.valeursRachat || []).length) continue;
      items.push({
        id: `3p-${c.id}`,
        kind: "3p",
        titulaire,
        label: `${c.type === "3B" ? "3B" : "3A"} – ${prenom} – ${c.compagnie || ""} – ${c.police || ""}`.replace(/\s+–\s+$/g, "").trim(),
        institution: c.compagnie || "3e pilier",
        montantDisponible: resolved.montant,
        capitalStatut: resolved.statut,
        pctCapital: 100,
        anneeRetraitPrevue: year,
        anneePremierePossible: year,
        anneeDernierePossible: null,
        sourceId: c.id,
      });
    }
  };
  push3p(
    "client1",
    input.client1.prenom || "assuré 1",
    input.client1.troisiemePilier,
  );
  if (input.conjoint) {
    push3p(
      "conjoint",
      input.conjoint.prenom || "Assuré 2",
      input.conjoint.troisiemePilier,
    );
  }

  return items;
}

/** Duplique un scénario (nouveaux ids) sans ressaisie. */
export function duplicateWithdrawalScenario(
  sc: WithdrawalScenario,
): WithdrawalScenario {
  const suffix = Math.random().toString(36).slice(2, 7);
  return {
    id: `sc-${Date.now()}-${suffix}`,
    name: `${sc.name} (copie)`,
    includeInReport: false,
    items: sc.items.map((it) => ({
      ...it,
      id: `${it.id}-copy-${suffix}`,
    })),
  };
}

export function newEmptyScenario(name = "Nouveau scénario"): WithdrawalScenario {
  return {
    id: `sc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name,
    includeInReport: false,
    items: [],
  };
}

export function kindLabel(kind: CapitalKind): string {
  switch (kind) {
    case "lpp":
      return "LPP";
    case "libre_passage":
      return "LP";
    case "3p":
      return "3P";
    default:
      return "Autre";
  }
}

/** 3A imposable au retrait, 3B exonéré. Un contrat sans type reste un 3A. */
export function thirdPillarTaxMark(
  input: AnalyseInput,
  item: { kind: string; sourceId?: string; label?: string },
): { type: "3A" | "3B"; exonere: boolean } | null {
  if (item.kind !== "3p") return null;
  const contracts = [
    ...(input.client1?.troisiemePilier || []),
    ...(input.conjoint?.troisiemePilier || []),
  ];
  const found = item.sourceId
    ? contracts.find((contract) => contract.id === item.sourceId)
    : undefined;
  const fromContract = String(found?.type ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
  const fromLabel = String(item.label || "").trim().toUpperCase();
  const type =
    fromContract === "3B" || (!found && fromLabel.startsWith("3B"))
      ? "3B"
      : "3A";
  return { type, exonere: type === "3B" };
}

export function capitalTypeText(
  input: AnalyseInput,
  item: { kind: string; sourceId?: string; label?: string },
): string {
  const pillar = thirdPillarTaxMark(input, item);
  if (!pillar) return kindLabel(item.kind as CapitalKind);
  return pillar.exonere ? "3B · exonéré" : "3A · imposable";
}

export function selectedWithdrawalYear(item: {
  anneePremierePossible?: number | null;
  anneeRetraitPrevue?: number | null;
}): number | null {
  if (item.anneePremierePossible != null && Number.isFinite(Number(item.anneePremierePossible))) {
    return Number(item.anneePremierePossible);
  }
  if (item.anneeRetraitPrevue != null && Number.isFinite(Number(item.anneeRetraitPrevue))) {
    return Number(item.anneeRetraitPrevue);
  }
  return null;
}

export function withPillarCapital(
  input: AnalyseInput,
  item: WithdrawalPlanItem,
): WithdrawalPlanItem {
  if (item.kind !== "3p" || !item.sourceId) return item;
  const person = item.titulaire === "conjoint" ? input.conjoint : input.client1;
  const contract = person?.troisiemePilier?.find((row) => row.id === item.sourceId);
  if (!contract) return item;
  const resolved = resolveThirdPillarCapital(contract, selectedWithdrawalYear(item));
  return {
    ...item,
    montantDisponible: resolved.montant,
    capitalStatut: resolved.statut,
  };
}
